import dayjs from "dayjs";
import { SlArrowLeft, SlArrowRight } from "react-icons/sl";
import { useShallow } from "zustand/react/shallow";

import DropdownYearMonthPicker from "@/components/DatePicker/DropdownYearMonthPicker";
import { scheduleYears } from "@/constants/calendar";
import useCalendarStore from "@/store/calendarStore";
import useUserStore from "@/store/userStore";
import type { ViewMode } from "@/types/store/calendarStore";
import { cn } from "@/utils/cn";

const viewModes: { label: string; value: ViewMode }[] = [
  { label: "일일", value: "daily" },
  { label: "주간", value: "weekly" },
  { label: "월간", value: "monthly" },
];

interface ViewModeToolbarProps {
  onDetail?: () => void;
  onReserve?: () => void;
  hasDetailData?: boolean;
  /** Override reserve button disabled state (in addition to past date check) */
  reserveDisabled?: boolean;
  /** Custom tooltip for disabled reserve button */
  reserveTooltip?: string;
}

const ViewModeToolbar = ({
  onDetail,
  onReserve,
  hasDetailData = true,
  reserveDisabled = false,
  reserveTooltip,
}: ViewModeToolbarProps) => {

  const [
    date,
    setDate,
    firstDayOfMonth,
    setFirstDayOfMonth,
    viewMode,
    setViewMode,
    resetCalendar,
  ] = useCalendarStore(
    useShallow(state => [
      state.date,
      state.setDate,
      state.firstDayOfMonth,
      state.setFirstDayOfMonth,
      state.viewMode,
      state.setViewMode,
      state.resetCalendar,
    ]),
  );

  const goPrevious = () => {
    if (viewMode === "daily") {
      const prev = date.subtract(1, "day");
      setDate(prev);
      if (prev.month() !== firstDayOfMonth.month()) {
        setFirstDayOfMonth(prev.date(1));
      }
    } else if (viewMode === "weekly") {
      const prev = date.subtract(7, "day");
      setDate(prev);
      if (prev.month() !== firstDayOfMonth.month()) {
        setFirstDayOfMonth(prev.date(1));
      }
    } else {
      const prev = date.date(0).date(1);
      setFirstDayOfMonth(prev);
      setDate(prev);
    }
  };

  const goNext = () => {
    if (viewMode === "daily") {
      const next = date.add(1, "day");
      setDate(next);
      if (next.month() !== firstDayOfMonth.month()) {
        setFirstDayOfMonth(next.date(1));
      }
    } else if (viewMode === "weekly") {
      const next = date.add(7, "day");
      setDate(next);
      if (next.month() !== firstDayOfMonth.month()) {
        setFirstDayOfMonth(next.date(1));
      }
    } else {
      const next = date.date(date.daysInMonth() + 1);
      setFirstDayOfMonth(next);
      setDate(next);
    }
  };

  const getDateLabel = () => {
    if (viewMode === "daily") {
      return date.format("YYYY년 M월 D일 (dd)");
    }
    if (viewMode === "weekly") {
      const weekStart = date.startOf("week");
      const weekEnd = date.endOf("week");
      const weekOfMonth = Math.ceil(weekStart.date() / 7);
      return `${weekStart.format("M")}월 ${weekOfMonth}주차 (${weekStart.format("M/D")} ~ ${weekEnd.format("M/D")})`;
    }
    return null; // monthly uses DropdownYearMonthPicker
  };

  const userStateCode = useUserStore(state => state.userStateCode);
  const isApproved = userStateCode === "01";
  const isPastDate = date.isBefore(dayjs(), "day");
  const isReserveDisabled = isPastDate || reserveDisabled;

  const getReserveTooltip = () => {
    if (reserveTooltip) return reserveTooltip;
    if (isPastDate) return "이전 날짜이므로 예약이 불가합니다.";
    return "";
  };

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      {/* View mode tabs */}
      <div className="flex gap-1.5">
        {viewModes.map(({ label, value }) => (
          <button
            key={value}
            type="button"
            onClick={() => setViewMode(value)}
            className={cn(
              "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
              viewMode === value
                ? "border-primary bg-primary text-white"
                : "border-gray-300 bg-white text-gray-500 hover:border-primary/50 hover:text-primary",
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Date navigation */}
      <div className="flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-1.5 py-1 shadow-sm">
        <button
          type="button"
          onClick={resetCalendar}
          className="rounded-full bg-white px-3 py-1 text-xs font-medium text-gray-600 shadow-sm transition-colors hover:bg-primary hover:text-white"
        >
          오늘
        </button>

        <div className="mx-1 h-4 w-px bg-gray-300" />

        <button
          type="button"
          onClick={goPrevious}
          className="flex h-7 w-7 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-white hover:text-primary hover:shadow-sm"
        >
          <SlArrowLeft className="text-[10px]" />
        </button>

        {viewMode === "monthly" ? (
          <DropdownYearMonthPicker
            years={scheduleYears}
            className="flex px-2 text-sm font-semibold text-gray-800 md:text-base"
            onClick={setDate}
          />
        ) : (
          <span className="min-w-[10rem] text-center text-sm font-semibold text-gray-800 md:text-base">
            {getDateLabel()}
          </span>
        )}

        <button
          type="button"
          onClick={goNext}
          className="flex h-7 w-7 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-white hover:text-primary hover:shadow-sm"
        >
          <SlArrowRight className="text-[10px]" />
        </button>
      </div>

      {/* 상세 & 예약신청 buttons */}
      <div className="flex gap-2">
        <div className="group relative">
          <button
            type="button"
            onClick={onDetail}
            disabled={!hasDetailData}
            className="rounded-full border border-gray-300 bg-white px-4 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:border-primary/50 hover:text-primary disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-gray-300 disabled:hover:text-gray-600"
          >
            상세
          </button>
          {!hasDetailData && (
            <div className="pointer-events-none absolute bottom-full left-1/2 mb-2 hidden -translate-x-1/2 whitespace-nowrap rounded-md bg-gray-800 px-3 py-1.5 text-xs text-white shadow-lg group-hover:block">
              내역이 존재하지 않습니다.
              <div className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
            </div>
          )}
        </div>
        {isApproved && (
          <div className="group relative">
            <button
              type="button"
              onClick={onReserve}
              disabled={isReserveDisabled}
              className="rounded-full bg-primary px-4 py-1.5 text-xs font-medium text-white shadow-sm transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40"
            >
              예약신청
            </button>
            {isReserveDisabled && getReserveTooltip() && (
              <div className="pointer-events-none absolute bottom-full left-1/2 mb-2 hidden -translate-x-1/2 whitespace-nowrap rounded-md bg-gray-800 px-3 py-1.5 text-xs text-white shadow-lg group-hover:block">
                {getReserveTooltip()}
                <div className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewModeToolbar;
